import { attachmentKey, type AttachmentTarget } from "@gjwtrack/domain";
import { useEffect, useMemo, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { client, useClient } from "../lib/client.ts";
import { issueKey } from "../lib/format.ts";
import { extractKeys, renderMarkdown } from "../lib/markdown.ts";

/**
 * Rendered Markdown with live issue links: `KEY-N` becomes a link, and a
 * resolved issue is struck through (YouTrack behaviour). Key lookups go
 * through the client store so later state changes update the strike.
 */
export function MarkdownView({ text, target, className = "" }: { text: string; target?: AttachmentTarget; className?: string }) {
  // Attachment links carry no extension (/api/attachments/ID); the owner's attachment list says which are videos.
  const atts = useClient((s) => (target === undefined ? undefined : s.attachments[attachmentKey(target)]?.items));
  const videoUrls = useMemo(() => new Set((atts ?? []).filter((a) => a.mime.startsWith("video/")).flatMap((a) => [`/api/attachments/${a.id}`, client.http.attachmentUrl(a.id)])), [atts]);
  const schema = useClient((s) => s.schema);
  // Only keys of projects that exist become links; with mixed-case keys allowed, "utf-8" must not.
  const projectKeys = useMemo(() => new Set((schema?.data.projects ?? []).map((p) => p.key.toLowerCase())), [schema]);
  const html = useMemo(() => renderMarkdown(text, { videoUrls, projectKeys }), [text, videoUrls, projectKeys]);
  const keys = useMemo(() => extractKeys(text, projectKeys), [text, projectKeys]);
  const keyIndex = useClient((s) => s.keyIndex);
  const display = useClient((s) => s.display);
  const navigate = useNavigate();

  useEffect(() => {
    if (keys.length && schema) void client.resolveKeys(keys, (i) => issueKey(schema, i));
  }, [keys, schema]);

  // Post-process: mark resolved keys and attach titles. Cheap: a handful of anchors per text.
  const decorated = useMemo(() => {
    if (keys.length === 0) return html;
    return html.replace(/<a class="issue-link" href="\/issue\/([A-Za-z0-9_-]+)" data-issue-key="([A-Za-z0-9_-]+)">/g, (m, _h, key: string) => {
      const id = keyIndex[key.toUpperCase()];
      const issue = id !== undefined && id > 0 ? display[id] : undefined;
      if (!issue) return m;
      const cls = issue.resolvedAt !== null ? "issue-link resolved" : "issue-link";
      const title = issue.summary.replace(/"/g, "&quot;");
      return `<a class="${cls}" href="/issue/${key}" data-issue-key="${key}" title="${title}">`;
    });
  }, [html, keys, keyIndex, display]);

  const onClick = (e: MouseEvent<HTMLDivElement>): void => {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    const key = a.getAttribute("data-issue-key");
    const articleKey = a.getAttribute("data-article-key");
    if (key) {
      e.preventDefault();
      navigate(`/issue/${key}`);
    } else if (articleKey) {
      e.preventDefault();
      navigate(`/article/${articleKey}`);
    } else if (a.getAttribute("href")?.startsWith("http")) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener");
    }
  };

  return <div className={`md ${className}`} onClick={onClick} dangerouslySetInnerHTML={{ __html: decorated }} />;
}
